import { HyruleToken } from '@nintendo/hyrule-react-commons';
import { PartOrderApiModel, PartOrderApiModelRequest } from './models';
import { BaseApi } from './BaseApi';
import { HttpStatusCode } from '../shared/HttpStatusCode';

export enum ProductType {
    Physical = 'Physical',
    Digital = 'Digital',
}

export enum OrderLineStatus {
    BOOKED = 'BOOKED',
    CANCELLED = 'CANCELLED',
    AWAITING_SHIPPING = 'AWAITING_SHIPPING',
    SHIPPED = 'SHIPPED',
    PICKED = 'PICKED',
    PICKED_PARTIAL = 'PICKED_PARTIAL',
    ENTERED = 'ENTERED',
    FULFILLED = 'FULFILLED',
    INVOICED = 'INVOICED',
    CLOSED = 'CLOSED',
    AWAITING_FULFILLMENT = 'AWAITING_FULFILLMENT',
    ORDERED = 'ORDERED',
    ALLOCATED = 'ALLOCATED',
    CANCELED = 'CANCELED',
    PARTIALLYFULFILLED = 'PARTIALLYFULFILLED',
    RETURNINITIATED = 'RETURNINITIATED',
    RETURNED = 'RETURNED',
}

export const DigitalOrderStatusMap: Map<OrderLineStatus, string> = new Map([
    [OrderLineStatus.BOOKED, 'Processing'],
    [OrderLineStatus.ENTERED, 'Processing'],
    [OrderLineStatus.FULFILLED, 'Code Delivered'],
    [OrderLineStatus.INVOICED, 'Code Delivered'],
    [OrderLineStatus.CLOSED, 'Code Delivered'],
    [OrderLineStatus.AWAITING_FULFILLMENT, 'Code Delivered'],
]);

export const PhysicalOrderStatusMap = new Map([
    [OrderLineStatus.BOOKED, 'Processing'],
    [OrderLineStatus.CANCELLED, 'Cancelled'],
    [OrderLineStatus.ENTERED, 'Processing'],
    [OrderLineStatus.INVOICED, 'Shipped'],
    [OrderLineStatus.CLOSED, 'Shipped'],
    [OrderLineStatus.AWAITING_SHIPPING, 'Processing'],
    [OrderLineStatus.SHIPPED, 'Shipped'],
    [OrderLineStatus.PICKED, 'Picked'],
    [OrderLineStatus.PICKED_PARTIAL, 'Picked'],
    [OrderLineStatus.ORDERED, 'Ordered'],
    [OrderLineStatus.ALLOCATED, 'Allocated'],
    [OrderLineStatus.CANCELED, 'Canceled'],
    [OrderLineStatus.FULFILLED, 'Fulfilled'],
    [OrderLineStatus.PARTIALLYFULFILLED, 'Partially Fulfilled'],
    [OrderLineStatus.RETURNINITIATED, 'Return Initiated'],
    [OrderLineStatus.RETURNED, 'Returned'],
]);

export const resolveApiUrl = (request: PartOrderApiModelRequest): string => {
    const { accountApiUrls } = BaseApi.ApiConfig();
    const { naid, email, orderNumber } = request;

    if (orderNumber) {
        return accountApiUrls.partOrdersByOrderNumber(orderNumber);
    }
    if (naid) {
        return accountApiUrls.partOrdersByNaid(naid);
    }
    if (email) {
        return accountApiUrls.partOrdersByEmail(encodeURIComponent(email));
    }
    throw new Error(`Could not resolve part orders API URL! ${JSON.stringify(request)}`);
};

export class PartOrderApi {
    static async getPartOrders(
        request: PartOrderApiModelRequest,
        token: HyruleToken,
        correlationId: string,
    ): Promise<PartOrderApiModel> {
        const url: string = resolveApiUrl(request);
        const options: RequestInit = {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token.accessToken || ''}`,
                'x-correlation-id': correlationId,
            },
        };

        try {
            const response: Response = await fetch(url, options);

            if (!response.ok && response?.status !== HttpStatusCode.NotFound) {
                throw new Error('Error getting part orders!');
            }
            return response.ok ? await response.json() : null;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }
}
